// @ts-nocheck
/**
 * The single way into the Live Sheet for new teams.
 *
 * The organiser picks the route every time — no remembered default — then
 * either pastes a block from their spreadsheet (auto-detected column mapping,
 * correctable) or types one team at a time. Both create tracked rows.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ClipboardList, UserPlus, ArrowLeft } from "lucide-react";
import {
  PASTE_FIELDS,
  buildTeams,
  detectMapping,
  parsePastedGrid,
  type PasteField,
  type PastedTeam,
} from "@/components/events/PasteTrackedTeamsDialog";
import { matchDivisionLabel } from "@/lib/teamNameClean";
import TeamNameEntry, { type DivisionOption } from "@/components/events/TeamNameEntry";
import { newItem } from "@/lib/registrationCart";

type Mode = "choose" | "paste" | "single";

type Props = {
  open: boolean;
  busy?: boolean;
  /** Division names offered in the single-team form. */
  ageGroups: string[];
  eventId: string;
  onOpenChange: (open: boolean) => void;
  /** Resolves true when the rows were written. */
  onAdd: (rows: PastedTeam[]) => Promise<boolean> | boolean;
};

const emptySingle = {
  contact_first: "",
  contact_last: "",
  contact_email: "",
  contact_phone: "",
};

export default function AddTeamsDialog({
  open,
  busy,
  eventId,
  ageGroups,
  onOpenChange,
  onAdd,
}: Props) {
  const [mode, setMode] = useState<Mode>("choose");
  const [text, setText] = useState("");
  const [mapping, setMapping] = useState<PasteField[]>([]);
  const [single, setSingle] = useState({ ...emptySingle });
  const [addedCount, setAddedCount] = useState(0);

  // Step 1 of registration, verbatim. It owns the team name, the matching, the
  // "None of these, it's a new team" path and the division — we only read the
  // result out of the cart item it commits.
  const [teamItem, setTeamItem] = useState(() => newItem({ event_id: eventId }));
  const [resolved, setResolved] = useState<{
    name: string;
    division_label: string;
  } | null>(null);

  const resetTeamEntry = () => {
    setTeamItem(newItem({ event_id: eventId }));
    setResolved(null);
  };

  // The Live Sheet only knows division names, so build the option list the
  // registration component expects from them.
  const divisionOptions: DivisionOption[] = useMemo(
    () =>
      ageGroups.map((label) => {
        const age = (label.match(/\b(\d{1,2}\s?u|u\s?\d{1,2})\b/i)?.[1] ?? label)
          .replace(/\s+/g, "")
          .toLowerCase();
        return {
          catalog_division_id: label,
          gender: /girl|women|ladies/i.test(label) ? "girls" : "boys",
          label,
          ageGroup: age,
          price: 0,
        } as DivisionOption;
      }),
    [ageGroups]
  );
  const optionKey = (o: DivisionOption) => `${o.catalog_division_id}:${o.gender}`;

  // The route is chosen fresh on every open.
  useEffect(() => {
    if (!open) return;
    setMode("choose");
    setText("");
    setMapping([]);
    setSingle({ ...emptySingle });
    setAddedCount(0);
    resetTeamEntry();
  }, [open, eventId]);

  const grid = useMemo(() => parsePastedGrid(text), [text]);

  useEffect(() => {
    if (grid.length > 0) setMapping(detectMapping(grid));
    else setMapping([]);
  }, [grid]);

  const rows = useMemo(
    () => (mapping.length ? buildTeams(grid, mapping, ageGroups) : []),
    [grid, mapping, ageGroups]
  );
  const valid = rows.filter((r) => !r.problem);
  const flagged = rows.filter((r) => r.problem);
  const preview = rows.slice(0, 5);

  const setColumn = (index: number, field: PasteField) => {
    setMapping((prev) => {
      const next = [...prev];
      if (field !== "ignore") {
        for (let i = 0; i < next.length; i++) if (next[i] === field) next[i] = "ignore";
      }
      next[index] = field;
      return next;
    });
  };

  const submitPaste = async () => {
    const ok = await onAdd(valid);
    if (ok) onOpenChange(false);
  };

  const submitSingle = async () => {
    if (!resolved?.name) return;
    const ok = await onAdd([
      {
        team_name: resolved.name,
        division_label: resolved.division_label || "",
        contact_name: [single.contact_first, single.contact_last]
          .map((s) => s.trim())
          .filter(Boolean)
          .join(" "),
        contact_email: single.contact_email.trim(),
        contact_phone: single.contact_phone.trim(),
        expected_amount: null,
        problem: null,
      },
    ]);
    if (ok) {
      // Keep the dialog open so several teams can go in one after another.
      setAddedCount((c) => c + 1);
      setSingle({ ...emptySingle });
      resetTeamEntry();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode !== "choose" && (
              <button
                type="button"
                onClick={() => setMode("choose")}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Back"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            Add teams
          </DialogTitle>
          <DialogDescription>
            {mode === "choose"
              ? "How would you like to add them?"
              : mode === "paste"
              ? "Paste from your spreadsheet — one team per line."
              : "Added teams are tracked leads until they're confirmed."}
          </DialogDescription>
        </DialogHeader>

        {mode === "choose" && (
          <div className="grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setMode("paste")}
              className="rounded-lg border p-4 text-left transition-colors hover:border-primary hover:bg-muted/40"
            >
              <ClipboardList className="mb-2 h-5 w-5 text-primary" />
              <div className="font-medium">Paste a list</div>
              <p className="mt-1 text-sm text-muted-foreground">
                Paste rows from a spreadsheet. Columns are detected and can be corrected
                before anything is added.
              </p>
            </button>
            <button
              type="button"
              onClick={() => setMode("single")}
              className="rounded-lg border p-4 text-left transition-colors hover:border-primary hover:bg-muted/40"
            >
              <UserPlus className="mb-2 h-5 w-5 text-primary" />
              <div className="font-medium">Add one team</div>
              <p className="mt-1 text-sm text-muted-foreground">
                Type a single team. The form clears after each save so you can keep going.
              </p>
            </button>
          </div>
        )}

        {mode === "paste" && (
          <div className="min-h-0 flex-1 space-y-3 overflow-auto">
            <Textarea
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={6}
              className="font-mono text-sm"
              placeholder={"Paste from your spreadsheet — one team per line.\n\nTeam Thunder\t14U\tJane\tDoe\tjane@example.com\t555-123-4567\t$250"}
            />

            {rows.length > 0 && (
              <>
                <div className="overflow-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 z-10 bg-muted/80">
                      <tr>
                        {mapping.map((field, i) => (
                          <th key={i} className="px-2 py-2 text-left font-medium">
                            <Select
                              value={field}
                              onValueChange={(v) => setColumn(i, v as PasteField)}
                            >
                              <SelectTrigger className="h-8 w-[150px] text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {PASTE_FIELDS.map((f) => (
                                  <SelectItem key={f.id} value={f.id} className="text-xs">
                                    {f.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </th>
                        ))}
                        <th className="px-3 py-2 text-left text-xs font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grid.slice(0, 5).map((cells, r) => (
                        <tr
                          key={r}
                          className={preview[r]?.problem ? "bg-destructive/10" : "border-t"}
                        >
                          {mapping.map((field, c) => (
                            <td
                              key={c}
                              className={
                                field === "ignore"
                                  ? "px-2 py-1.5 text-muted-foreground line-through"
                                  : "px-2 py-1.5"
                              }
                            >
                              {cells[c] ?? ""}
                            </td>
                          ))}
                          <td className="px-3 py-1.5 text-xs">
                            {preview[r]?.problem ? (
                              <span className="text-destructive">{preview[r]?.problem}</span>
                            ) : (
                              <span className="text-muted-foreground">Ready</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-muted-foreground">
                  {rows.length > 5 && <>Showing the first 5 of {rows.length} pasted rows. </>}
                  {flagged.length > 0 && (
                    <span className="text-destructive">
                      {flagged.length} {flagged.length === 1 ? "row needs" : "rows need"}{" "}
                      attention and won't be added.
                    </span>
                  )}
                </p>
              </>
            )}
          </div>
        )}

        {mode === "single" && (
          <div
            data-testid="add-teams-body"
            className="grid min-h-0 flex-1 gap-3 overflow-y-auto pr-1 sm:grid-cols-2"
          >

            <div className="sm:col-span-2">
              <Label>Team name</Label>
              <div className="mt-1">
                {resolved ? (
                  <div className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <div className="text-base font-semibold">{resolved.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {resolved.division_label || "No division"}
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={resetTeamEntry}>
                      Change
                    </Button>
                  </div>
                ) : (
                  <TeamNameEntry
                    item={teamItem}
                    options={divisionOptions}
                    optionKey={optionKey}
                    clubs={[]}
                    eventYear={new Date().getFullYear()}
                    showPrice={false}
                    successMessage={(n) => `${n} ready to add`}
                    allowRemove={false}
                    createOnRecruitLook={false}
                    showVariationOnLowConfidence
                    updateItem={(patch) => setTeamItem((t) => ({ ...t, ...patch }))}
                    onCommit={(patch) => {
                      const merged = { ...teamItem, ...patch } as any;
                      const label =
                        merged.division_label ||
                        matchDivisionLabel(ageGroups, merged.age_group ?? null, null) ||
                        "";
                      setTeamItem(merged);
                      setResolved({
                        name: merged.team_display_name || merged.team_name || "",
                        division_label: label,
                      });
                    }}
                    onDiscard={resetTeamEntry}
                  />
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="at-first">Coach first</Label>
              <Input
                id="at-first"
                value={single.contact_first}
                onChange={(e) => setSingle((s) => ({ ...s, contact_first: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="at-last">Coach last</Label>
              <Input
                id="at-last"
                value={single.contact_last}
                onChange={(e) => setSingle((s) => ({ ...s, contact_last: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="at-email">Email</Label>
              <Input
                id="at-email"
                type="email"
                value={single.contact_email}
                onChange={(e) => setSingle((s) => ({ ...s, contact_email: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="at-phone">Phone</Label>
              <Input
                id="at-phone"
                value={single.contact_phone}
                onChange={(e) => setSingle((s) => ({ ...s, contact_phone: e.target.value }))}
              />
            </div>
            {addedCount > 0 && (
              <p className="sm:col-span-2 text-sm text-muted-foreground">
                {addedCount} {addedCount === 1 ? "team" : "teams"} added. The form is ready
                for the next one.
              </p>
            )}
          </div>
        )}

        {mode !== "choose" && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
              {mode === "single" && addedCount > 0 ? "Done" : "Cancel"}
            </Button>
            {mode === "paste" ? (
              <Button onClick={submitPaste} disabled={busy || valid.length === 0}>
                {busy
                  ? "Adding…"
                  : `Add ${valid.length} ${valid.length === 1 ? "team" : "teams"}`}
              </Button>
            ) : (
              <Button onClick={submitSingle} disabled={busy || !resolved?.name}>
                {busy ? "Adding…" : "Save and add another"}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
